import { useEffect, useState } from 'react';
import { APP_LANGUAGES_ENUM } from 'config/constants';
import translations from 'config/languages';
import { getChaptersByNumberAndBookIds, getChaptersVerses } from 'core/services/bible/chapters.service';
import { logError } from 'config/logging';
import { IBibleBook } from 'types';
import { useBooks, useGetBibleUrlParams } from 'core/hooks';

import type { INormalizedChapters, IVersesRow } from './types';
import { NOTES_VALUE } from './Header/TranslationsSelector';

const addWordChapter = (text: string, number: number, language?: APP_LANGUAGES_ENUM) => {
  if (text.replace(/ /g, '').length < 3 && language) {
    return `${translations[language].chapter} ${number}`;
  }

  return text;
};

const getReadingPageData = async (
  translationsBooks: IBibleBook[],
  chapterNumber: number,
): Promise<INormalizedChapters | null> => {
  try {
    const booksIds = translationsBooks.map(book => book.id);
    const chaptersData = await getChaptersByNumberAndBookIds(booksIds, chapterNumber);
    const sortedChapters = chaptersData.sort(
      (a, b) => booksIds.indexOf(a.book_id) - booksIds.indexOf(b.book_id),
    );
    const sortedChaptersIds = sortedChapters.map(({ id }) => id);
    const verses = await getChaptersVerses(sortedChaptersIds);

    return {
      chapters: sortedChapters.map(chapter => ({
        ...chapter,
        title: addWordChapter(
          chapter.title,
          chapter.number,
          translationsBooks.find(book => book.id === chapter.book_id)?.language,
        ),
      })),
      versesRows: verses.reduce((result, verse) => {
        const rowIndex = sortedChaptersIds.indexOf(verse.chapter_id);
        if (!result[verse.number]) {
          result[verse.number] = Array(translationsBooks.length).fill({ text: '', id: 0 });
        }

        result[verse.number][rowIndex] = { text: verse?.text, id: verse?.id, title: verse?.title, is_start_of_paragraph: verse?.is_start_of_paragraph, cross_reference: verse?.cross_reference };

        return result;
      }, [Array(translationsBooks.length).fill({ text: '', id: 0 })] as IVersesRow[][]),
      versesCount: verses.length > 0 ? new Set(verses.map(v => v.number)).size : 0,
    };
  } catch (e) {
    logError(e, 'getReadingPageData');
    return null;
  }
};

export type GetBooksByNumberAndTranslationId = (
  bookNumber: number,
  translationId: number,
) => Promise<IBibleBook[]>;

export function useReadingPageData() {
  const { book, chapter, translations: translationsParam } = useGetBibleUrlParams();
  const { getBooksByNumberAndTranslationId } = useBooks();
  const [books, setBooks] = useState<IBibleBook[]>([]);
  const [readingData, setReadingData] = useState<INormalizedChapters | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!book || !chapter || !translationsParam) return;

    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);

        const translationIds = translationsParam
          .split(',')
          .filter(part => part !== NOTES_VALUE)
          .map(Number)
          .filter(n => !Number.isNaN(n));

        const booksByTranslations = await Promise.all(
          translationIds.map(translationId => getBooksByNumberAndTranslationId(book, translationId)),
        );
        const translationsBooks = booksByTranslations.flat();

        if (cancelled) return;

        setBooks(translationsBooks);

        const data = await getReadingPageData(translationsBooks, chapter);

        if (cancelled) return;

        setReadingData(data);
      } catch (err) {
        if (!cancelled) {
          logError(err, 'Error fetching reading page data');
          setReadingData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [book, chapter, translationsParam, getBooksByNumberAndTranslationId]);

  const versesCount = readingData?.versesCount ?? 0;

  return { books, readingData, loading, versesCount };
}
